<script lang="ts" setup generic="O = any, V = any">
import { computed, nextTick, shallowRef, watch, type VNodeProps, type VNode, type ComponentPublicInstance } from 'vue';
import { unrefElement, whenever } from '@vueuse/core';
import { IconSearch, IconClose } from '@/icons';
import { useFocusZone, useOptionsList, type OptionsListItem, type OptionsListGroup } from '../../composables';
import { Keys } from '../../shared/enums';
import { vFocus } from '../../directives';
import { getClosestFocusable, hasSlotContent } from '../../utils';
import { ObSeparator } from '../separator';
import { UiButtonIcon } from '../ui-button-icon';
import { ObScrollableContainer } from '../scrollable-container';
import { ObSpinner } from '../spinner';
import {
  ObActionListContextProvider,
  ObActionList,
  ObActionListItem,
  ObActionListGroup,
  ObActionListGroupList,
} from '../action-list';
import { ObPrimitiveInput } from '../primitive-input';
import { ObHostedDropdown } from '../hosted-dropdown';
import type { OptionsListProps } from '../../shared/types';
import type { Path } from '../../../shared/types';

type Props = OptionsListProps<O, V> & {
  open?: boolean;
  title?: string;
  subtitle?: string;
  withSearch?: boolean;
  withGroups?: boolean;
  hideEmptyGroups?: boolean;
  withSelectAll?: boolean;
  searchPlaceholder?: string;
  groups?: {
    key: string;
    title?: string | null;
  }[];
  optionGroup?: Path<O> | ((option: O) => string);
  selectionMode?: 'single' | 'multiple';
} & ({ selectionMode?: 'single'; modelValue?: V | null } | { selectionMode?: 'multiple'; modelValue?: V[] });

defineOptions({
  inheritAttrs: false,
});

const {
  title,
  subtitle,
  withSearch = false,
  withGroups = false,
  hideEmptyGroups = true,
  withSelectAll = false,
  searchPlaceholder,
  selectionMode = 'single',
  options = [],
  asyncOptions,
  groups,
  optionDisabled,
  optionLabel,
  optionValue,
  hydrateValue,
  optionGroup,
  trackValueBy,
  optionsLimit,
  selectedFirst,
} = defineProps<Props>();

const emit = defineEmits<{
  select: [value: (V | null) | V[]]; // TODO: correct types base on selection mode?
  'update:modelValue': [value: (V | null) | V[]]; // TODO: correct types base on selection mode?
  'update:search': [query: string];
  open: []; // TODO: trigger
  close: []; // TODO: trigger
  activated: []; // TODO: trigger
  deactivated: []; // TODO: trigger
}>();

defineSlots<{
  host?: (props: { open: boolean; hostProps: VNodeProps }) => VNode;
  default?: (props: {
    optionsList: OptionsListItem<O, V>[];
    optionsGroups: OptionsListGroup<O, V>[];
    selectOption: (option: OptionsListItem<O, V>) => void;
    toggleOption: (option: OptionsListItem<O, V>) => void;
    search: string;
    selectAll: () => void;
    toggleAll: () => void;
    allOptionsSelected: boolean;
    selectedOptions: OptionsListItem<O, V>[];
  }) => VNode;
  header?: () => VNode;
  footer?: () => VNode;
}>();

// TODO: is it possible to use `useTemplateRef` with Function Refs?
const hostRef = shallowRef<HTMLElement | null>(null);
const containerRef = shallowRef<HTMLElement | null>(null);
const optionsRef = shallowRef<HTMLElement | null>(null);
const searchInputRef = shallowRef<HTMLInputElement | null>(null);

const open = defineModel<boolean>('open', { default: false }); // TODO: rename to active
const modelValue = defineModel<V | null | V[]>({ default: null });
const search = defineModel<string>('search', { default: '' });

// TODO: do not emit here
watch(open, (value) => {
  if (value) {
    emit('open');
    return;
  }

  emit('close');
});

let activeDescendant: HTMLElement | undefined;

const { focusFirst, focusLast } = useFocusZone({
  container: optionsRef,
  activeDescendantControl: searchInputRef,
  onActiveDescendantChanged: (current, previous, directlyActivated) => {
    activeDescendant = current;

    if (directlyActivated) {
      activeDescendant?.scrollIntoView({ block: 'nearest', inline: 'start' });
    }
  },
});

function activate() {
  open.value = true;
  emit('activated');
}

function deactivate() {
  open.value = false;
  emit('deactivated');
}

function toggle() {
  if (open.value) {
    deactivate();
    return;
  }

  activate();
}

function onEsc(event: KeyboardEvent) {
  if (open.value) {
    event.preventDefault();
    event.stopPropagation();
    open.value = false;
    nextTick(() => {
      hostRef.value?.focus();
    });
  }
}

function onTab(event: KeyboardEvent) {
  event.preventDefault();
  event.stopImmediatePropagation();

  open.value = false;

  nextTick(() => {
    if (!hostRef.value) {
      return;
    }

    const target =
      getClosestFocusable({
        initial: hostRef.value,
        root: document.documentElement,
        previous: event.shiftKey,
      }) ?? hostRef.value;

    target?.focus();
  });
}

function onHostClick(event: MouseEvent) {
  event.preventDefault();
  open.value = !open.value;
}

function onHostKeydown(event: KeyboardEvent) {
  if (event.key === Keys.ArrowDown) {
    event.preventDefault();
    open.value = true;
    setTimeout(() => {
      focusFirst();
    });
    return;
  }

  if (event.key === Keys.ArrowUp) {
    event.preventDefault();
    open.value = true;
    setTimeout(() => {
      focusLast();
    });
    return;
  }
}

function onItemSelect() {
  emit('select', modelValue.value);

  if (selectionMode !== 'single') {
    return;
  }

  open.value = false;
  nextTick(() => {
    hostRef.value?.focus();
  });
}

function onSearchInputKeydown(event: KeyboardEvent) {
  if (event.key === Keys.Enter && activeDescendant) {
    event.preventDefault();
    event.stopImmediatePropagation();

    // Forward Enter key press to active descendant so that item gets activated
    const activeDescendantEvent = new KeyboardEvent(event.type, event);
    activeDescendant.dispatchEvent(activeDescendantEvent);
  }
}

const hostProps = computed(() => ({
  onClick: onHostClick,
  onKeydown: onHostKeydown,
  'aria-haspopup': true,
  'aria-expanded': open.value,
}));

function onClickClose(event: MouseEvent) {
  event.preventDefault();
  open.value = !open.value;
}

const {
  optionsList,
  optionsGroups,
  optionsGroupsWithEmpty,
  allOptionsSelected,
  selectedOptions,
  loadingOptions,
  loadOptions,
  selectOption,
  toggleOption,
  selectAll,
  toggleAll,
} = useOptionsList<O, V>({
  asyncOptions,
  options: computed(() => options),
  groups,
  optionDisabled,
  optionLabel,
  optionValue,
  optionGroup,
  hydrateValue,
  trackValueBy,
  selectionMode,
  search,
  optionsLimit,
  selected: modelValue,
  updateSelected(value) {
    modelValue.value = value;
  },
  selectedFirst,
});

const visibleOptionsGroups = computed(() => (hideEmptyGroups ? optionsGroups.value : optionsGroupsWithEmpty.value));

whenever(
  open,
  () => {
    if (asyncOptions) {
      loadOptions();
    }
  },
  { once: true, immediate: true },
);

defineExpose({
  activate,
  deactivate,
  toggle,
});
</script>

<template>
  <ObHostedDropdown
    v-model:active="open"
    tabindex="-1"
    role="dialog"
    shift
    flip
    focus-trap
    :padding="5"
    :size="{
      apply({ availableHeight, elements }) {
        Object.assign(elements.floating.style, {
          maxHeight: `${Math.max(0, availableHeight)}px`,
        });
      },
    }"
    @keydown.esc="onEsc"
    @keydown.tab="onTab"
    @click-outside="open = false"
  >
    <template #host="{ hostProps: dropdownHostProps }">
      <slot
        name="host"
        v-bind="{
          open,
          hostProps: {
            ...dropdownHostProps,
            ...hostProps,
            ref(el: Element | ComponentPublicInstance | null) {
              dropdownHostProps.ref(el);
              hostRef = unrefElement(el as any); // TODO: how not to cast no any? Element vs HTMLElement
            },
          },
        }"
      />
    </template>
    <div ref="containerRef" :class="$style.container">
      <div :class="$style.header">
        <div :class="$style.headerTop">
          <div :class="$style.headerContent">
            <div v-if="title" :class="$style.title">{{ title }}</div>
            <slot name="header" />
          </div>
          <UiButtonIcon size="s" variant="tertiary" tabindex="-1" @click="onClickClose">
            <IconClose aria-hidden="true" />
          </UiButtonIcon>
        </div>
        <div v-if="subtitle" :class="$style.subtitle">
          {{ subtitle }}
        </div>
        <div v-if="withSearch">
          <ObPrimitiveInput v-focus size="s" clearable>
            <input
              ref="searchInputRef"
              v-model="search"
              type="text"
              :placeholder="searchPlaceholder"
              aria-haspopup="listbox"
              autocomplete="off"
              @keydown="onSearchInputKeydown"
            />
            <template #icon>
              <IconSearch aria-hidden="true" />
            </template>
          </ObPrimitiveInput>
        </div>
      </div>
      <ObSeparator />
      <ObScrollableContainer light overscroll-behavior="contain">
        <div ref="optionsRef">
          <div v-if="loadingOptions" :class="$style.noData">
            <ObSpinner size="48px" />
            Loading
          </div>
          <div v-else-if="!optionsList.length" :class="$style.noData">
            {{ search ? `No items found for '${search}'` : 'No items found' }}
          </div>
          <ObActionListContextProvider v-else list-role="listbox" item-role="option" :on-after-select="onItemSelect">
            <ObActionList :selection-mode="selectionMode" compact>
              <ObActionListItem
                v-if="withSelectAll && !search"
                :selected="allOptionsSelected"
                :indeterminate="!allOptionsSelected && selectedOptions.length > 0"
                @select="toggleAll()"
              >
                <span :class="$style.selectAll">All</span>
              </ObActionListItem>
              <slot
                v-bind="{
                  optionsList,
                  optionsGroups: visibleOptionsGroups,
                  selectOption,
                  toggleOption,
                  search,
                  selectAll,
                  toggleAll,
                  allOptionsSelected,
                  selectedOptions,
                }"
              >
                <template v-if="withGroups">
                  <ObActionListGroup v-for="group in optionsGroups" :key="group.key" :title="group.title">
                    <ObActionListGroupList>
                      <ObActionListItem
                        v-for="option in group.options"
                        :key="option.label"
                        :selected="option.selected"
                        :disabled="option.disabled"
                        @select="selectionMode === 'single' ? selectOption(option) : toggleOption(option)"
                      >
                        {{ option.label }}
                      </ObActionListItem>
                    </ObActionListGroupList>
                  </ObActionListGroup>
                </template>
                <template v-else>
                  <ObActionListItem
                    v-for="option in optionsList"
                    :key="option.label"
                    :selected="option.selected"
                    :disabled="option.disabled"
                    @select="selectionMode === 'single' ? selectOption(option) : toggleOption(option)"
                  >
                    {{ option.label }}
                  </ObActionListItem>
                </template>
              </slot>
            </ObActionList>
          </ObActionListContextProvider>
        </div>
      </ObScrollableContainer>
      <template v-if="hasSlotContent($slots.footer)">
        <ObSeparator />
        <div :class="$style.footer">
          <slot name="footer" />
        </div>
      </template>
    </div>
  </ObHostedDropdown>
</template>

<style lang="scss" module>
@use '../../styles/colors';
@use '../../styles/shared';
@use '../../styles/typography';

.container {
  display: flex;
  flex-direction: column;
  min-width: 300px;
  max-width: 480px;
  max-height: 480px;
  flex: 1;
  overflow: auto;
}

.header {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.headerTop {
  display: flex;
  align-items: center;
  justify-content: end;
}

.headerContent {
  flex-basis: 0;
  flex-grow: 1;
  max-width: 100%;
  min-width: 0;
  padding-right: 12px;
}

.title {
  color: #021148;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
}

.subtitle {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: #9aa0b6;
}

.noData {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  gap: 4px;
  padding: 48px 8px;
}

.selectAll {
  font-weight: 600;
}

.footer {
  padding: 12px;
}
</style>
